import { useState } from 'react';
import SectionHeading from '@/pages/home/components/SectionHeading';
import { addressList } from '@/mocks/home';

const statusMap: Record<string, { label: string; className: string; dot: string }> = {
  available: {
    label: '可用',
    className: 'bg-primary-100 text-primary-800',
    dot: 'bg-primary-500',
  },
  backup: {
    label: '备用',
    className: 'bg-accent-100 text-accent-800',
    dot: 'bg-accent-500',
  },
  offline: {
    label: '已停用',
    className: 'bg-background-200 text-foreground-500',
    dot: 'bg-foreground-400',
  },
};

export default function AddressList() {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopy = async (id: string, domain: string) => {
    try {
      await navigator.clipboard.writeText(`https://${domain}`);
    } catch {
      // 剪贴板不可用时仅做视觉反馈
    }
    setCopiedId(id);
    window.setTimeout(() => {
      setCopiedId((current) => (current === id ? null : current));
    }, 1600);
  };

  return (
    <section id="address" className="w-full px-4 md:px-10 py-14 md:py-20 bg-background-50">
      <SectionHeading
        index="03"
        eyebrow="Latest Address"
        title="最新地址 · 收录与状态一览"
        desc="下列地址按校验时间排列，并标注当前状态。点「复制网址」即可拿到完整地址，打不开时优先切换状态为「可用」的线路。"
      />

      <div className="mt-10 rounded-lg border border-background-200 bg-background-100 overflow-hidden">
        <div className="hidden md:grid grid-cols-[2.2fr_1.6fr_0.9fr_1fr_auto] gap-4 px-5 py-3 bg-background-200/70 font-label text-xs text-foreground-500">
          <span>收录名称</span>
          <span>官方网址</span>
          <span>状态</span>
          <span>校验时间</span>
          <span className="text-right">操作</span>
        </div>

        <div className="divide-y divide-background-200">
          {addressList.map((item) => {
            const status = statusMap[item.status] ?? statusMap.available;
            const isCopied = copiedId === item.id;
            return (
              <div
                key={item.id}
                className="grid grid-cols-1 md:grid-cols-[2.2fr_1.6fr_0.9fr_1fr_auto] gap-3 md:gap-4 items-center px-5 py-4 hover:bg-background-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${status.dot}`} aria-hidden="true"></span>
                  <div className="min-w-0">
                    <p className="font-heading font-bold text-sm text-foreground-950 truncate">
                      {item.name}
                    </p>
                    <p className="font-label text-xs text-foreground-500 truncate md:hidden">
                      {item.note}
                    </p>
                  </div>
                </div>

                <div className="min-w-0">
                  <span className="font-label text-sm text-foreground-700 break-all">
                    {item.domain}
                  </span>
                  <span className="hidden md:block font-label text-xs text-foreground-400 mt-0.5">
                    {item.note}
                  </span>
                </div>

                <div>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-label text-xs ${status.className}`}>
                    {status.label}
                  </span>
                </div>

                <span className="font-label text-xs md:text-sm text-foreground-500">
                  {item.updated}
                </span>

                <div className="flex md:justify-end">
                  <button
                    type="button"
                    onClick={() => handleCopy(item.id, item.domain)}
                    className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md border font-label text-xs transition-colors cursor-pointer whitespace-nowrap ${
                      isCopied
                        ? 'border-primary-300 bg-primary-50 text-primary-700'
                        : 'border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700'
                    }`}
                  >
                    <i className={isCopied ? 'ri-check-line' : 'ri-file-copy-line'} aria-hidden="true"></i>
                    {isCopied ? '已复制' : '复制网址'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <p className="mt-4 flex items-start gap-2 font-label text-xs text-foreground-500">
        <i className="ri-information-line text-sm mt-0.5" aria-hidden="true"></i>
        提示：状态为「已停用」的地址仅作历史记录，请勿继续使用；如全部线路异常，请稍后再回来看更新。
      </p>
    </section>
  );
}